var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { Fragment, useEffect, useState, useCallback, forwardRef } from 'react';
import cx from 'classnames';
import Text, { TextSize } from '@move-ui/text';
import { ChevronSmallFilledIcon, CheckmarkIcon } from '@move-ui/icons-asset-catalog';
import styles from './NativeDropdown.module.css';
import { Label } from '../Label/Label';
import { ErrorMessage } from '../ErrorMessage/ErrorMessage';
import { flatMap } from '../utils/arrayFunctions';
/**
 * @deprecated
 * Direct import and use of this component from move-ui/form is discouraged in favor
 * of Dropdown component. NativeDropdown is meant to be used only as an internal or as a
 * fallback component on the Dropdown.
 */
export const NativeDropdown = forwardRef((_a, parentRef) => {
    var { id, name, className, selectClassName, disabled, isLabelScreenReaderOnly, label, onChange, onBlur, options, sections, placeholder, required, value, tabIndex = 0, isValid = true, applySuccessState, errorMessage, onClick, onOpen, onClose, iconClassName, selectedIcon, multiple, labelExpandsWidth, appliedValues = [], displayLabel, blockDoubleOpen } = _a, rest = __rest(_a, ["id", "name", "className", "selectClassName", "disabled", "isLabelScreenReaderOnly", "label", "onChange", "onBlur", "options", "sections", "placeholder", "required", "value", "tabIndex", "isValid", "applySuccessState", "errorMessage", "onClick", "onOpen", "onClose", "iconClassName", "selectedIcon", "multiple", "labelExpandsWidth", "appliedValues", "displayLabel", "blockDoubleOpen"]);
    const [isOpen, setIsOpen] = useState(false);
    useEffect(() => {
        import('focus-visible').catch(() => null);
    }, []);
    const handleClick = useCallback((event) => {
        if (blockDoubleOpen) {
            const { currentTarget } = event;
            // Firefox on Android ignores preventDefault and disabling and immediately
            // enabling the select is the only way to stop the native Select Menu from opening
            currentTarget.disabled = true;
            setTimeout(() => {
                currentTarget.disabled = false;
            });
        }
        if (!isOpen) {
            onOpen === null || onOpen === void 0 ? void 0 : onOpen();
            setIsOpen(true);
        }
        onClick === null || onClick === void 0 ? void 0 : onClick(event);
    }, [isOpen, onOpen, onClick, blockDoubleOpen]);
    const handleOnBlur = useCallback((event) => {
        if (isOpen) {
            onClose === null || onClose === void 0 ? void 0 : onClose();
            setIsOpen(false);
        }
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(event);
    }, [isOpen, onClose, onBlur]);
    const handleChange = useCallback((event) => {
        if (multiple) {
            handleClick(event);
            return;
        }
        if (isOpen) {
            onClose === null || onClose === void 0 ? void 0 : onClose();
            setIsOpen(false);
        }
        onChange === null || onChange === void 0 ? void 0 : onChange(event);
    }, [isOpen, onClose, onChange, multiple, handleClick]);
    const showError = !(isValid || disabled) && errorMessage;
    const isSuccessfulSelect = applySuccessState && isValid && !disabled && value;
    const allOptions = (sections === null || sections === void 0 ? void 0 : sections.length)
        ? flatMap(sections, ({ values }) => values)
        : options || [];
    const getDisplayLabel = () => {
        var _a, _b;
        if (displayLabel) {
            return displayLabel;
        }
        if (Array.isArray(value)) {
            return (((_a = allOptions
                .filter((option) => appliedValues.includes(option.value))
                .map((option) => option.label)
                .filter((val, index, self) => self.indexOf(val) === index)) === null || _a === void 0 ? void 0 : _a.join(', ')) || placeholder);
        }
        return (((_b = allOptions.find((option) => option.value === value)) === null || _b === void 0 ? void 0 : _b.label) ||
            placeholder);
    };
    return (React.createElement(Fragment, null,
        React.createElement("div", { className: cx(styles.component, {
                [styles['component--disabled']]: disabled,
                [styles['component--isInvalid']]: !(isValid || disabled),
                [styles['component--isSuccessful']]: isSuccessfulSelect,
                [styles['component--hasIcon']]: selectedIcon,
                [styles['component--isSuccessfulWithIcon']]: isSuccessfulSelect && selectedIcon,
                [styles.withLabel]: label && !isLabelScreenReaderOnly
            }, className) },
            label && (React.createElement(Label, { htmlFor: id, className: cx(styles.label, {
                    [styles.disabled]: disabled,
                    [styles.opaque]: multiple && !disabled
                }), isScreenReaderOnly: isLabelScreenReaderOnly, applyDisabledStyle: disabled }, label)),
            isSuccessfulSelect && (React.createElement(CheckmarkIcon, { "data-testid": "CheckmarkIcon", className: styles.checkmarkIcon })),
            selectedIcon && (React.createElement("div", { className: isSuccessfulSelect
                    ? styles.selectedIcon
                    : styles.selectedIconNoSuccess }, selectedIcon)),
            React.createElement("select", Object.assign({ id: id, className: cx(selectClassName, styles.select, {
                    [styles.transparent]: multiple,
                    [styles.labelExpandsWidth]: labelExpandsWidth,
                    [styles.withLabel]: label && !isLabelScreenReaderOnly
                }), disabled: disabled, name: name, required: required, value: value, onChange: handleChange, onBlur: handleOnBlur, onClick: handleClick, tabIndex: tabIndex, ref: parentRef, multiple: multiple }, rest),
                !required && !multiple && (React.createElement("option", { key: `${name}-empty`, value: "" }, placeholder)),
                sections &&
                    sections.map((section) => (React.createElement("optgroup", { label: section.label, key: `${name}-${section.label}` }, section.values.map((option) => (React.createElement("option", { key: `${name}-${option.value}`, value: option.value }, option.label || option.value)))))),
                !sections &&
                    (options === null || options === void 0 ? void 0 : options.map((option) => (React.createElement("option", { key: `${name}-${option.value}`, value: option.value }, option.label || option.value))))),
            React.createElement(Text, { size: TextSize.M, color: disabled ? 'neutral-200' : 'neutral-100', className: cx(styles.displayLabel, {
                    [styles.opaque]: multiple && !disabled,
                    [styles.disabledBackground]: multiple && disabled,
                    [styles.displayLabelWithIconSuccess]: isSuccessfulSelect,
                    [styles.displayLabelWithIconSelect]: selectedIcon && !applySuccessState,
                    [styles.displayLabelWithIconSuccessAndSelect]: applySuccessState && selectedIcon,
                    [styles.labelExpandsWidth]: labelExpandsWidth,
                    [styles.withLabel]: label && !isLabelScreenReaderOnly
                }) }, getDisplayLabel()),
            React.createElement(ChevronSmallFilledIcon, { "data-testid": "ChevronSmallFilledIcon", className: cx(styles.icon, { [styles.disabled]: disabled }, iconClassName) })),
        showError && errorMessage && (React.createElement(ErrorMessage, { id: `${id}-errorMessage`, message: errorMessage }))));
});
//# sourceMappingURL=NativeDropdown.js.map